"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Card, Button } from "@nextui-org/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import LeftArrowIcon from "@/helpers/svg/leftArrowIcon.svg";
import { ROUTES } from "@/routes/appRoutes";
import Link from "next/link";
import ContentTag from "./tag";
import { getCMSImageURL } from "@/helpers/CMSImageURL";
import classNames from "classnames";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";
import { isContentAllowedForAgeRange } from "@/helpers/ageRange.utils";
import ResponsiveImage from "../elements/ResponsiveImage";
import { setAutoResumeCookie } from "@/utils/autoResumeCookie";
import { useContentImpressionAnalytics } from "@/hooks/useMediaAnalytics";
import {
  buildContentAnalyticsPayload,
  sendPlayIntent,
} from "@/services/mediaAnalyticsService";
import { getArtistImageURL } from "@/helpers/artist.util";
import LiveStartCountdown from "@/components/common/LiveStartCountdown";
import NextPlayGuideHelper from "@/helpers/nextPlayGuideHelper";
import { isComingSoon } from "@/utils/contentStatus";
import { orderArtistItemsByPriority } from "@/helpers/artistPriority.utils";
import {
  getCountdownParts,
  getLiveStartTimestamp,
  isLiveStartDateValue,
} from "@/utils/liveStartCountdown";

const CONSTANTS = {
  KIND_GUIDE: {
    movie: "فیلم",
    series: "سریال",
    program: "برنامه",
    sport: "ورزشی",
    music: "موزیک",
    animation: "انیمیشن",
  },

  ASPECT_RATIOS: {
    movie: "aspect-[9/13]",
    searchbox: "aspect-[9/13]",
    series: "aspect-[9/13]",
    program: "aspect-video",
    sport: "aspect-video",
    music: "aspect-square",
    animation: "aspect-square",
    artist: "aspect-square",
  },

  GRID_SIZES: {
    movie: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
    searchbox: "grid-cols-2 md:grid-cols-3 lg:grid-cols-3",
    series: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
    program: "grid-cols-1 md:grid-cols-2 lg:grid-cols-5",
    music: "grid-cols-3 md:grid-cols-5 lg:grid-cols-7",
    animation: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
    artist: "grid-cols-3 md:grid-cols-5 lg:grid-cols-7",
  },

  PLACEHOLDERS: {
    artist: "/artist_placeholder.png",
    default: "/placeholder.jpg",
  },
};

const getImageSrc = (contentPhotos, isArtist, type, image) => {
  const fallbackImage = isArtist
    ? CONSTANTS.PLACEHOLDERS.artist
    : CONSTANTS.PLACEHOLDERS.default;

  const resolveArtistImage = (imageValue) => {
    if (!imageValue) return null;
    if (
      imageValue.startsWith("http://") ||
      imageValue.startsWith("https://") ||
      imageValue.startsWith("/")
    ) {
      return imageValue;
    }
    return getArtistImageURL(imageValue);
  };

  const getPhotoByType = (types) => {
    for (const photoType of types) {
      const photo = contentPhotos?.find((x) => x.type === photoType);
      if (photo?.src) return photo.src;
    }
    return image;
  };

  if (isArtist) {
    const artistImage =
      image ||
      getPhotoByType(["artist", "banner", "image", "avatar", "profile"]) ||
      contentPhotos?.find((photo) => photo?.src)?.src;

    return resolveArtistImage(artistImage) || fallbackImage;
  }

  let photoTypes;

  if (type === "music") {
    photoTypes = ["squareCover", "verticalCover"];
  } else if (type === "sport") {
    photoTypes = ["horizontalSportCover", "verticalCover"];
  } else {
    photoTypes = ["verticalCover", "verticalCover"];
  }

  const cdnOptions =
    type === "program" || type === "sport" ? {} : { width: 180 };
  return (
    getCMSImageURL(getPhotoByType(photoTypes), cdnOptions) || fallbackImage
  );
};

const CARD_CDN_OPTIONS_DEFAULT = { width: 280 };
const CARD_CDN_OPTIONS_WIDE = { hint: "none", q: 90 };

const isLiveValue = (value) => value === true || value === 1 || value === "1";

const getContentKind = (item = {}) =>
  item.kind?.kind ||
  (typeof item.kind === "string" ? item.kind : null) ||
  item.contentType?.kind ||
  (typeof item.contentType === "string" ? item.contentType : null) ||
  "movie";

const isLiveContent = (item = {}) => {
  const liveInfo = item.liveInfo || item.live;
  const tags = item.tags || [];

  return (
    isLiveValue(item.isLive) ||
    getContentKind(item) === "live" ||
    tags.includes("پخش زنده") ||
    Boolean(
      liveInfo &&
        (liveInfo.liveStartDate ||
          liveInfo.startDate ||
          liveInfo.streamKey ||
          liveInfo.liveEventId)
    )
  );
};

const OptimizedImage = ({
  contentPhotos,
  isArtist,
  type,
  image,
  title,
  tempAspects,
  className,
}) => {
  const resolvedImageSrc = isArtist
    ? getImageSrc(contentPhotos, isArtist, type, image)
    : null;
  const isArtistPlaceholder =
    isArtist && resolvedImageSrc === CONSTANTS.PLACEHOLDERS.artist;

  return (
    <div
      className={classNames(
        "!max-w-full w-full relative overflow-hidden cursor-pointer",
        tempAspects,
        isArtist &&
          "!w-[50px] !h-[50px] md:!w-[100px] md:!h-[100px] mr-1 md:mr-2",
        isArtist ? "rounded-full" : "rounded-none",
        !isArtist && "shadow-md",
        isArtistPlaceholder && "content-artist-avatar-placeholder",
        className
      )}
    >
      {isArtist ? (
        <img
          loading="lazy"
          src={resolvedImageSrc}
          alt={title || "Content image"}
          className={classNames(
            "object-cover w-full h-full border-2 border-zinc-600",
            "content-artist-avatar-image rounded-full",
            isArtistPlaceholder && "content-artist-placeholder-image"
          )}
        />
      ) : (
        <ResponsiveImage
          className="object-cover w-full h-full"
          alt=""
          fallbackSrc={getCMSImageURL(image) || CONSTANTS.PLACEHOLDERS.default}
          contentPhotos={contentPhotos}
          imageType={
            type === "music"
              ? "squareCover"
              : type === "sport"
              ? "horizontalSportCover"
              : "verticalCover"
          }
          cdnOptions={
            type === "program" || type === "sport"
              ? CARD_CDN_OPTIONS_WIDE
              : CARD_CDN_OPTIONS_DEFAULT
          }
        />
      )}
    </div>
  );
};

export const CardInfoView = ({
  title,
  year,
  country,
  imdb_rate,
  kind,
  type,
  isLive,
  children,
}) => {
  const shouldShowYear =
    year && year > 0 && !(isLive && isLiveStartDateValue(year));

  return (
    <div className="info flex flex-col gap-0 justify-end items-start text-sm w-full max-w-full">
      {type === "sport" && (
        <>
          <p className="font-bold mb-0 max-w-full truncate">{title}</p>
          {children}
        </>
      )}
      <p>{CONSTANTS.KIND_GUIDE[kind]}</p>
      {kind !== "music" && type !== "music" && (
        <>
          <p>
            {shouldShowYear && <span>{year}</span>}
            {country && " - " + country?.join()}
          </p>
          {!isLive && kind !== "sport" && imdb_rate > 0 && (
            <div className="flex gap-1 items-center">
              <img src="/imdb-icon.svg" alt="imdb" />
              <p className="m-0 font-sans">{imdb_rate}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CardItem = ({
  item,
  index,
  isArtist,
  type,
  tempAspects,
  categoryName,
  effectAction,
  getCardLink,
  hasShadow,
  showTitle = true,
  isSwiper,
  isContinueWatch = false,
  analyticsContext,
  onItemSelect,
  renderItemDetails,
}) => {
  const router = useRouter();
  const { content, artist, contentMeta } = item;
  const _item = contentMeta?.content?.meta?.bin || content || artist || item;

  const {
    contentPhotos,
    images,
    tags,
    title,
    titleEN,
    profession,
    farsiName,
    image,
    _id,
  } = _item;

  const effectiveIsLive = isLiveContent(_item);
  const liveStartTimestamp = effectiveIsLive
    ? getLiveStartTimestamp(_item)
    : null;
  const [hasActiveLiveCountdown, setHasActiveLiveCountdown] = useState(() =>
    Boolean(!isComingSoon(_item.status) && liveStartTimestamp && getCountdownParts(liveStartTimestamp))
  );
  const finalKind = getContentKind(_item);
  const comingSoon = isComingSoon(_item.status);
  const displayTags = Array.isArray(tags)
    ? tags.filter((tag) => tag !== "بزودی")
    : [];
  const impressionRef = useContentImpressionAnalytics({
    content: _item,
    surface:
      analyticsContext?.surface ||
      (isContinueWatch ? "continue_watch" : "content_list"),
    source: analyticsContext?.source || (isSwiper ? "carousel" : "grid"),
    containerKey: analyticsContext?.containerKey || categoryName,
    position: index,
    enabled: !isArtist && analyticsContext?.enabled !== false,
    minimumVisibleMs: analyticsContext?.minimumVisibleMs,
    requireAuth: analyticsContext?.requireAuth,
  });

  // Continue-watch data (flattened by useHomeShowcaseData)
  const elapsedTime = _item.elapsedTime || 0;
  const totalTime = _item.totalTime || 0;
  const seasonNumber = _item.seasonNumber || null;
  const episodeNumber = _item.episodeNumber || null;
  const progressPercent =
    totalTime > 0 ? Math.min((elapsedTime / totalTime) * 100, 100) : 0;
  const hasProgress = totalTime > 0 && elapsedTime > 0 && isContinueWatch;
  const hasEpisodeInfo = isContinueWatch && seasonNumber && episodeNumber;

  // For continue-watch: navigate to player; otherwise normal content page
  const getContinueWatchLink = () => {
    const slug = _item.slug;
    if (seasonNumber && episodeNumber) {
      return `/player/${slug}?s=${seasonNumber}&e=${episodeNumber}`;
    }
    return `/player/${slug}`;
  };

  const handleClick = (e) => {
    onItemSelect?.(_item, index);
    if (isContinueWatch) {
      e.preventDefault();
      if (comingSoon) {
        router.push(getCardLink(_item, { contentKey: item.contentKey }));
        return;
      }
      const payload = buildContentAnalyticsPayload(_item, {
        surface: "continue_watch",
        source: "content_card",
      });
      if (payload) sendPlayIntent(payload);
      // Set auto-resume cookie before navigating to player
      setAutoResumeCookie({
        slug: _item.slug,
        season: seasonNumber ? String(seasonNumber) : null,
        episode: episodeNumber ? String(episodeNumber) : null,
      });
      router.push(getContinueWatchLink());
      return;
    }
    if (typeof effectAction === "function") {
      effectAction(item.title || title);
    }
  };

  const linkHref = isContinueWatch
    ? comingSoon
      ? getCardLink(_item, { contentKey: item.contentKey })
      : getContinueWatchLink()
    : getCardLink(_item, { contentKey: item.contentKey });
  const showOverlayCountdown = !comingSoon && liveStartTimestamp && type !== "sport";
  const showSportCountdown = !comingSoon && liveStartTimestamp && type === "sport";

  useEffect(() => {
    setHasActiveLiveCountdown(
      Boolean(!comingSoon && liveStartTimestamp && getCountdownParts(liveStartTimestamp))
    );
  }, [liveStartTimestamp, comingSoon]);

  const handleCountdownVisibilityChange = useCallback((isVisible) => {
    setHasActiveLiveCountdown(isVisible);
  }, []);

  return (
    <Link
      ref={impressionRef}
      className={classNames(
        "relative focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white",
        !isArtist && "pt-3 mb-1 lg:mb-2",
        isArtist &&
          "content-artist-card !flex-row !flex !items-center bg-zinc-800 py-0.5 md:py-1 rounded-full",
        type === "sport" && "block mb-3"
      )}
      href={linkHref}
      key={_id || categoryName + finalKind + index + (titleEN || title)}
      onClick={handleClick}
    >
      {finalKind === "series" && (
        <div
          className={classNames(
            "absolute right-[5px] left-[5px] h-full",
            isSwiper ? "top-[-17px]" : "top-[-5px]"
          )}
          style={{
            backgroundImage: 'url("/serial-pseudo.svg")',
            backgroundSize: "100%",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}

      <Card
        className={classNames(
          "!bg-transparent shadow-none flex justify-center items-center relative w-full",
          isArtist && "content-artist-card-media",
          !isArtist && "card-with-hover lg:mt-3",
          hasActiveLiveCountdown &&
            type !== "sport" &&
            "card-with-live-countdown"
        )}
      >
        {comingSoon ? (
          <ContentTag label="بزودی" type={type} tags={["بزودی"]} />
        ) : (
          displayTags.length > 0 && (
            <ContentTag label={displayTags[0]} type={type} tags={displayTags} />
          )
        )}

        {!isArtist && (
          <CardInfoView {..._item} isLive={effectiveIsLive} type={type}>
            {type === "sport" && renderItemDetails?.(item)}
          </CardInfoView>
        )}

        <OptimizedImage
          contentPhotos={images || contentPhotos}
          isArtist={isArtist}
          type={type}
          image={image || _item.poster || _item.thumbnail}
          isLive={effectiveIsLive}
          title={title}
          tempAspects={tempAspects}
          className={
            isArtist
              ? "content-artist-avatar border-2 border-solid border-[#262626] w-20 md:w-40"
              : ""
          }
        />

        {showOverlayCountdown && (
          <LiveStartCountdown
            startTimestamp={liveStartTimestamp}
            className="content-card-countdown absolute bottom-2 left-1/2 z-[15] -translate-x-1/2 justify-center"
            onVisibilityChange={handleCountdownVisibilityChange}
          />
        )}

        {/* Episode badge for series continue-watch items */}
        {hasEpisodeInfo && (
          <div
            className="absolute z-[15] rounded-lg px-2.5 py-1 shadow-lg"
            style={{
              bottom: hasProgress ? "24px" : "8px",
              left: "8px",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            }}
          >
            <span
              className="text-white text-xs font-semibold whitespace-nowrap"
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
            >
              فصل {seasonNumber} • قسمت {episodeNumber}
            </span>
          </div>
        )}

        {/* Progress bar for continue-watch */}
        {hasProgress && (
          <div
            className="absolute bottom-2.5 left-2.5 right-2.5 h-1.5 rounded-full overflow-hidden z-[15]"
            style={{ backgroundColor: "rgb(91, 91, 91)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${progressPercent}%`,
                backgroundColor: "#E50914",
                marginRight: "auto",
              }}
            />
          </div>
        )}
      </Card>

      {showSportCountdown && (
        <LiveStartCountdown
          startTimestamp={liveStartTimestamp}
          size="sportCard"
          className="mt-2 justify-center"
        />
      )}

      {hasShadow && (
        <div className="with-shadow w-[90%] mx-auto mt-2">
          <img width={348} height={7} alt="shadow" src="/tp-shadow.png" />
        </div>
      )}

      {showTitle && type !== "sport" && (
        <div
          className={classNames(
            "flex flex-col w-full mr-2",
            isArtist
              ? "content-artist-copy items-center justify-center mr-0 pl-5 md:pl-10"
              : "items-start justify-start"
          )}
        >
          <span
            className={classNames(
              "text-sm truncate mt-2 w-full",
              !isArtist && "md:text-lg text-primary-white",
              isArtist &&
                "content-artist-profession text-[10px] md:text-lg text-[#BFBFBF]"
            )}
          >
            {isArtist ? profession : title}
          </span>
          {renderItemDetails?.(item)}

          {(isArtist || type === "music") && (
            <span
              className={classNames(
                "text-xs truncate w-full",
                !isArtist && "md:text-sm text-[#BFBFBF]",
                isArtist &&
                  "content-artist-name text-[9px] mb-1 md:mb-2 md:text-medium text-primary-white"
              )}
            >
              {isArtist && farsiName}
              {type === "music" && item.artistName}
            </span>
          )}
        </div>
      )}

      {type === "sport" && <div className="lg:py-4" />}
    </Link>
  );
};

const SWIPER_BREAKPOINTS = {
  base: {
    300: { slidesPerView: 2.5, spaceBetween: 10 },
    768: { slidesPerView: 4.5, spaceBetween: 20 },
    992: { slidesPerView: 6, spaceBetween: 20 },
    1200: { slidesPerView: 7, spaceBetween: 25 },
    1440: { slidesPerView: 7, spaceBetween: 25 },
  },
  sport: {
    300: { slidesPerView: 1.5, spaceBetween: 10 },
    768: { slidesPerView: 2.5, spaceBetween: 20 },
    992: { slidesPerView: 3.5, spaceBetween: 20 },
    1200: { slidesPerView: 3.5, spaceBetween: 25 },
    1440: { slidesPerView: 4.5, spaceBetween: 25 },
  },
  artist: {
    300: { slidesPerView: "auto", spaceBetween: 10 },
    768: { slidesPerView: "auto", spaceBetween: 15 },
    992: { slidesPerView: "auto", spaceBetween: 15 },
    1200: { slidesPerView: "auto", spaceBetween: 15 },
    1440: { slidesPerView: "auto", spaceBetween: 15 },
  },
  animation: {
    300: { slidesPerView: 1.9, spaceBetween: 10 },
    768: { slidesPerView: 3.5, spaceBetween: 20 },
    992: { slidesPerView: 6.5, spaceBetween: 25 },
    1200: { slidesPerView: 6.5, spaceBetween: 25 },
    1440: { slidesPerView: 6.5, spaceBetween: 25 },
  },
};

const getSwiperBreakpoints = (type, noContainer) => {
  const breakpoints = SWIPER_BREAKPOINTS[type] || SWIPER_BREAKPOINTS.base;
  const offsetBefore = noContainer
    ? 0
    : { 300: 20, 768: 40, 992: 60, 1200: 70, 1440: 80 };

  return Object.entries(breakpoints).reduce((acc, [key, value]) => {
    acc[key] = {
      ...value,
      slidesOffsetBefore: offsetBefore[key] || 0,
      slidesOffsetAfter: offsetBefore[key] || 0,
    };
    return acc;
  }, {});
};

const shuffleItems = (sourceItems) => {
  const shuffledItems = [...sourceItems];

  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffledItems[index], shuffledItems[randomIndex]] = [
      shuffledItems[randomIndex],
      shuffledItems[index],
    ];
  }

  return shuffledItems;
};

const Cards = ({
  items,
  categoryName,
  shape,
  type,
  isSwiper,
  isLive,
  isArtist = false,
  hasExpand,
  hasShadow,
  hasCategory,
  noMX,
  effectAction,
  showTitle,
  actionLink,
  noShadow,
  noContainer,
  isContinueWatch = false,
  preserveOrder = false,
  disableNavigation = false,
  minCountToShowNavigation = 5,
  nextPlayGuide,
  analyticsContext,
  onItemSelect,
  renderItemDetails,
  filterByAgeRange = true,
}) => {
  const swiperRef = useRef(null);
  const [swiperNavigationState, setSwiperNavigationState] = useState({
    isBeginning: true,
    isEnd: false,
  });
  const params = useSearchParams();
  const kind = params.get("kind");
  const router = useRouter();
  const _ageRange = useSelector((state) => state.Auth.ageRange) || 120;

  const tempAspects = CONSTANTS.ASPECT_RATIOS[type];
  const shapeSizes = CONSTANTS.GRID_SIZES[type];

  const parseActionLink = (link) => {
    if (link?.includes("kind")) {
      return link;
    } else {
      if (kind) {
        return `${link}?kind=${kind}`;
      } else {
        return link;
      }
    }
  };

  function getCardLink(item, options = {}) {
    const itemIsLive = isLiveContent(item);

    if (isArtist) {
      return ROUTES.artists + "/" + (item.slug || item.artist?.slug);
    } else if (itemIsLive) {
      return (
        ROUTES.live + "/" + (item.slug || options.contentKey || item.title)
      );
    } else {
      const route = ROUTES[getContentKind(item)] || ROUTES.movie;
      const slug = item.slug || options.contentKey;
      if (slug) {
        const contentUrl = route + "/" + slug;
        return getContentKind(item) === "music"
          ? NextPlayGuideHelper.addNextPlayGuideToUrl(contentUrl, nextPlayGuide)
          : contentUrl;
      } else {
        return route + "/" + item.title + "?oa=true";
      }
    }
  }

  const _items = useMemo(() => {
    if (!filterByAgeRange) return items;
    return items.filter(
      (x) =>
        isContentAllowedForAgeRange(x.content?.ageRange, _ageRange, {
          allowEmpty: false,
        }) ||
        isContentAllowedForAgeRange(
          x.contentMeta?.content?.ageRange,
          _ageRange,
          {
            allowEmpty: false,
          }
        ) ||
        isContentAllowedForAgeRange(x.ageRange, _ageRange, {
          allowEmpty: false,
        })
    );
  }, [items, _ageRange, filterByAgeRange]);

  const hasData = useMemo(() => {
    if (!filterByAgeRange) return items.length > 0;
    return items.some((x) =>
      isContentAllowedForAgeRange(x.content?.ageRange, _ageRange, {
        allowEmpty: false,
      })
    );
  }, [items, _ageRange, filterByAgeRange]);

  const itemsToRender = useMemo(
    () => (isArtist ? orderArtistItemsByPriority(items) : _items),
    [_items, isArtist, items]
  );

  const displayItems = useMemo(() => {
    if (isArtist || !isSwiper || isContinueWatch || preserveOrder) {
      return itemsToRender;
    }

    return shuffleItems(itemsToRender);
  }, [isArtist, isSwiper, isContinueWatch, preserveOrder, itemsToRender]);

  const swiperOrderKey = useMemo(() => {
    if (!preserveOrder && !isArtist) return "randomized";

    return (
      displayItems
        ?.map(
          (item, index) =>
            item.contentKey ||
            item.content?.contentKey ||
            item.slug ||
            item.content?.slug ||
            item._id ||
            item.content?._id ||
            index
        )
        .join("|") || "empty"
    );
  }, [displayItems, isArtist, preserveOrder]);

  const updateSwiperNavigationState = useCallback((swiper) => {
    if (!swiper) return;

    setSwiperNavigationState({
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
    });
  }, []);

  const getSlidesPerViewAmount = useCallback((swiper) => {
    const slidesPerView = swiper?.params?.slidesPerView;

    if (slidesPerView === "auto") {
      return Math.max(1, swiper.slidesPerViewDynamic?.() || 1);
    }

    return Math.max(1, Math.ceil(Number(slidesPerView) || 1));
  }, []);

  const handleNavigationClick = useCallback(
    (direction) => {
      const swiper = swiperRef.current;
      if (!swiper) return;

      const slidesPerView = getSlidesPerViewAmount(swiper);
      const nextIndex =
        direction === "next"
          ? swiper.activeIndex + slidesPerView
          : swiper.activeIndex - slidesPerView;

      swiper.slideTo(nextIndex);
    },
    [getSlidesPerViewAmount]
  );

  const renderNavigation = () => {
    if (
      !isSwiper ||
      disableNavigation ||
      displayItems?.length < minCountToShowNavigation
    )
      return null;
    return (
      <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-20 hidden md:block">
        {!swiperNavigationState.isBeginning && (
          <button
            type="button"
            aria-label="Previous slides"
            onClick={() => handleNavigationClick("prev")}
            className="pointer-events-auto absolute right-0 top-1/2 flex h-10 w-10 2xl:translate-x-[60px] -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-white/15 text-primary-white shadow-lg backdrop-blur-sm transition hover:bg-white/25"
          >
            <FaChevronRight size={16} />
          </button>
        )}
        {!swiperNavigationState.isEnd && (
          <button
            type="button"
            aria-label="Next slides"
            onClick={() => handleNavigationClick("next")}
            className="pointer-events-auto absolute left-0 top-1/2 flex h-10 w-10 2xl:-translate-x-[60px] -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-white/15 text-primary-white shadow-lg backdrop-blur-sm transition hover:bg-white/25"
          >
            <FaChevronLeft size={16} />
          </button>
        )}
      </div>
    );
  };

  if (!isArtist && !hasData && isSwiper) {
    return null;
  }

  return (
    <div
      className={classNames(
        "w-full cards",
        isArtist && "content-artist-cards",
        !noContainer && "max-w-[1920px] mx-auto"
      )}
    >
      {hasExpand && (
        <div
          className={classNames(
            "flex justify-between items-center mb-4",
            isArtist && "content-artist-heading",
            !noContainer &&
              "mr-[20px] md:mr-[40px] lg:mr-[60px] xl:mr-[70px] 2x:mr-[80px] pl-4"
          )}
        >
          <Link
            href={parseActionLink(actionLink)}
            className={classNames(
              "text-primary-white xs:text-base ms:text-xl lg:text-[32px] font-medium",
              !noContainer && "xs:pr-2 lg:pr-7"
            )}
          >
            {categoryName}
          </Link>
          <div className="flex items-center">
            <Button
              className="text-primary-white align-center pl-1 xs:text-base"
              color="primary"
              variant="light"
              endContent={<LeftArrowIcon />}
              onPress={() => router.push(parseActionLink(actionLink))}
            >
              مشاهده بیشتر
            </Button>
          </div>
        </div>
      )}
      {!hasExpand && showTitle && (
        <div
          className={classNames(
            "flex justify-between items-center mb-4",
            !noContainer &&
              "mr-[20px] md:mr-[40px] lg:mr-[60px] xl:mr-[70px] 2x:mr-[80px] pl-4"
          )}
        >
          <h4
            className={classNames(
              "text-primary-white xs:text-base ms:text-xl lg:text-[32px] font-medium",
              !noContainer && "xs:pr-2 lg:pr-7"
            )}
          >
            {categoryName}
          </h4>
        </div>
      )}
      {hasCategory && !isLive && (
        <div
          className={classNames(
            "flex justify-between items-center mb-4",
            !noContainer &&
              "mr-[20px] md:mr-[40px] lg:mr-[60px] xl:mr-[70px] 2x:mr-[80px] pl-4"
          )}
        >
          <h4
            className={classNames(
              "text-primary-white xs:text-base ms:text-xl lg:text-[32px] font-medium",
              !noContainer && "xs:pr-2 lg:pr-7"
            )}
          >
            {categoryName}
          </h4>
        </div>
      )}
      {isSwiper ? (
        <div
          className={classNames(
            "relative overflow-hidden md:mt-5 md:overflow-visible",
            isArtist && "content-artist-carousel rounded-full"
          )}
        >
          {renderNavigation()}
          <Swiper
            key={swiperOrderKey}
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            onInit={updateSwiperNavigationState}
            onSlideChange={updateSwiperNavigationState}
            onReachBeginning={updateSwiperNavigationState}
            onReachEnd={updateSwiperNavigationState}
            onFromEdge={updateSwiperNavigationState}
            onBreakpoint={updateSwiperNavigationState}
            onResize={updateSwiperNavigationState}
            className="w-full px-1"
            modules={[Navigation]}
            breakpoints={getSwiperBreakpoints(type, noContainer)}
            pagination={null}
            freeMode
          >
            {displayItems?.map((item, index) => (
              <SwiperSlide
                key={
                  item._id || item.content?._id || categoryName + type + index
                }
                className={isArtist ? "max-w-fit" : "max-w-full pt-5"}
              >
                <CardItem
                  item={item}
                  index={index}
                  isArtist={isArtist}
                  type={type}
                  tempAspects={tempAspects}
                  categoryName={categoryName}
                  effectAction={effectAction}
                  getCardLink={getCardLink}
                  hasShadow={hasShadow}
                  isSwiper={isSwiper}
                  isContinueWatch={isContinueWatch}
                  analyticsContext={analyticsContext}
                  onItemSelect={onItemSelect}
                  renderItemDetails={renderItemDetails}
                />
              </SwiperSlide>
            ))}
            {!noShadow && !isArtist && <div className="SwiperEndGradient" />}
          </Swiper>
        </div>
      ) : (
        <div
          className={classNames(
            "w-full grid justify-center items-center xl:justify-start gap-3 md:gap-3 lg:gap-5 px-1",
            shapeSizes,
            !noMX && "px-4 md:px-0"
          )}
        >
          {itemsToRender?.map((item, index) => (
            <CardItem
              key={item._id || item.content?._id || categoryName + type + index}
              item={item}
              index={index}
              isArtist={isArtist}
              type={type}
              tempAspects={tempAspects}
              categoryName={categoryName}
              effectAction={effectAction}
              getCardLink={getCardLink}
              hasShadow={hasShadow}
              showTitle={shape !== "sport"}
              isSwiper={isSwiper}
              analyticsContext={analyticsContext}
              onItemSelect={onItemSelect}
              renderItemDetails={renderItemDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Cards;
